import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { getTranslation } from '../translations';
import AdminBlogManagement from '../components/AdminBlogManagement';
import AdminAnalytics from '../components/AdminAnalytics';
import AdminReferrals from '../components/AdminReferrals';
import MyChildren from '../components/MyChildren';
import { useTheme, THEMES } from '../contexts/ThemeContext';
import './Settings.css';

export default function Settings({ user, logout, updateUser, refreshUser }) {
  const navigate = useNavigate();
  const t = (key) => getTranslation(user?.language || 'en', key);
  const { theme, updateTheme, themes } = useTheme();
  const [savingTheme, setSavingTheme] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    language: user?.language || 'en'
  });
  const [kindleEmail, setKindleEmail] = useState(user?.send_to_kindle_email || '');
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [childPhotoFile, setChildPhotoFile] = useState(null);
  const [childPhotoPreview, setChildPhotoPreview] = useState(user?.child_photo_url || null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [emailPreferences, setEmailPreferences] = useState(
    user?.email_preferences || {
      weekly_reminders: true,
      story_completion: true,
      subscription_updates: true
    }
  );
  const [resetUserId, setResetUserId] = useState('');
  const [subscriptionUserId, setSubscriptionUserId] = useState('');
  const [targetTier, setTargetTier] = useState('pro');
  const [bonusUserId, setBonusUserId] = useState('');
  const [bonusAmount, setBonusAmount] = useState(1);

  const isPro = user?.subscription_tier === 'pro';

  useEffect(() => {
    // Refresh user data on mount to ensure latest subscription info
    if (refreshUser) {
      refreshUser();
    }
    fetchSubscriptionInfo();
  }, []);

  useEffect(() => {
    setProfile({
      name: user?.name || '',
      email: user?.email || '',
      language: user?.language || 'en'
    });
    setKindleEmail(user?.send_to_kindle_email || '');
    setChildPhotoPreview(user?.child_photo_url || null);
    setEmailPreferences(user?.email_preferences || {
      weekly_reminders: true,
      story_completion: true,
      subscription_updates: true
    });
  }, [user]);

  const fetchSubscriptionInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription info');
    }
  };

  const handleCancelSubscription = async () => {
    const confirmMessage = isGerman 
      ? 'Sind Sie sicher, dass Sie Ihr Pro-Abonnement kündigen möchten? Sie können Pro-Funktionen bis zum Ende Ihres bezahlten Zeitraums weiterhin nutzen.'
      : 'Are you sure you want to cancel your Pro subscription? You can continue using Pro features until the end of your paid period.';
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show success message with expiration date
      const expiresAt = response.data.expires_at;
      const expirationDate = new Date(expiresAt).toLocaleDateString(isGerman ? 'de-DE' : 'en-US');
      const successMessage = isGerman
        ? `Abonnement gekündigt. Sie können Pro-Funktionen noch bis zum ${expirationDate} nutzen.`
        : `Subscription cancelled. You can use Pro features until ${expirationDate}.`;
      
      toast.success(successMessage, { duration: 6000 });
      if (refreshUser) await refreshUser();
      fetchSubscriptionInfo();
    } catch (error) {
      toast.error(t('toasts.failedToCancelSubscription'));
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('toasts.profileUpdated'));
      if (updateUser) updateUser({ ...user, ...profile });
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error(t('toasts.failedToUpdateProfile'));
    }
  };

  const handleUpdateKindleEmail = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/kindle-email`, 
        { send_to_kindle_email: kindleEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('toasts.kindleEmailUpdated'));
      if (updateUser) updateUser({ ...user, send_to_kindle_email: kindleEmail });
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error(t('toasts.failedToUpdateKindleEmail'));
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      isGerman 
        ? 'Bist du absolut sicher? Dies löscht dein Konto und alle Geschichten dauerhaft. Diese Aktion kann nicht rückgängig gemacht werden.'
        : 'Are you absolutely sure? This will permanently delete your account and all stories. This action cannot be undone.'
    );
    
    if (confirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/auth/delete-account`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(isGerman ? 'Konto erfolgreich gelöscht' : 'Account deleted successfully');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      } catch (error) {
        console.error('Error deleting account:', error);
        toast.error(isGerman ? 'Fehler beim Löschen des Kontos' : 'Failed to delete account');
      }
    }
  };

  const handleEmailPreferencesChange = async (key, value) => {
    const newPrefs = { ...emailPreferences, [key]: value };
    setEmailPreferences(newPrefs);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/email-preferences`, newPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isGerman ? 'E-Mail-Einstellungen aktualisiert' : 'Email preferences updated');
      
      // Refresh user to get updated preferences
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error updating email preferences:', error);
      toast.error(isGerman ? 'Fehler beim Aktualisieren der Einstellungen' : 'Failed to update preferences');
      // Revert on error
      setEmailPreferences(emailPreferences);
    }
  };

  const handleExportData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/export-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create downloadable JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `storytales-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(isGerman ? 'Daten erfolgreich exportiert' : 'Data exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(isGerman ? 'Fehler beim Exportieren der Daten' : 'Failed to export data');
    }
  };

  const handleChildPhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      
      setChildPhotoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setChildPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChildPhotoUpload = async () => {
    if (!childPhotoFile) {
      toast.error('Please select a photo first');
      return;
    }

    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', childPhotoFile);

      const response = await axios.post(`${API}/settings/child-photo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      updateUser(response.data);
      setChildPhotoPreview(response.data.child_photo_url);
      setChildPhotoFile(null);
      toast.success(isGerman ? 'Kindfoto erfolgreich hochgeladen!' : 'Child photo uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleChildPhotoDelete = async () => {
    const confirmed = window.confirm(
      isGerman 
        ? 'Möchtest du das Kindfoto wirklich löschen?'
        : 'Are you sure you want to delete the child photo?'
    );
    
    if (confirmed) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API}/settings/child-photo`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        updateUser(response.data);
        setChildPhotoPreview(null);
        setChildPhotoFile(null);
        toast.success(isGerman ? 'Kindfoto gelöscht' : 'Child photo deleted');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete photo');
      }
    }
  };

  const handleResetUserStoryCount = async (e) => {
    e.preventDefault();
    const isGermanTemp = user?.language === 'de';
    
    if (!window.confirm(isGermanTemp ? `Story-Anzahl für Benutzer ${resetUserId} wirklich zurücksetzen?` : `Are you sure you want to reset story count for user ${resetUserId}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/reset-user-story-count/${resetUserId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isGermanTemp ? `Story-Anzahl für ${response.data.user_email} erfolgreich zurückgesetzt` : `Story count reset successfully for ${response.data.user_email}`);
      setResetUserId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isGermanTemp ? 'Fehler beim Zurücksetzen der Story-Anzahl' : 'Failed to reset story count'));
    }
  };

  const handleGrantBonusStories = async (e) => {
    e.preventDefault();
    const isGermanTemp = user?.language === 'de';
    
    if (!window.confirm(isGermanTemp 
      ? `Wirklich ${bonusAmount} Bonus-Geschichten an Benutzer ${bonusUserId} vergeben?` 
      : `Are you sure you want to grant ${bonusAmount} bonus stories to user ${bonusUserId}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/grant-bonus-stories`, {
        user_identifier: bonusUserId,
        amount: parseInt(bonusAmount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isGermanTemp 
        ? `${response.data.bonus_stories_granted} Bonus-Geschichten erfolgreich an ${response.data.user_email} vergeben. Verbleibend: ${response.data.bonus_stories_remaining}` 
        : `Successfully granted ${response.data.bonus_stories_granted} bonus stories to ${response.data.user_email}. Remaining: ${response.data.bonus_stories_remaining}`);
      setBonusUserId('');
      setBonusAmount(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isGermanTemp ? 'Fehler beim Vergeben der Bonus-Geschichten' : 'Failed to grant bonus stories'));
    }
  };

  const handleChangeSubscriptionTier = async (e) => {
    e.preventDefault();
    const isGermanTemp = user?.language === 'de';
    
    const tierText = targetTier === 'pro' ? 'Pro' : 'Free';
    if (!window.confirm(isGermanTemp 
      ? `Abonnement für Benutzer ${subscriptionUserId} wirklich auf ${tierText} ändern?` 
      : `Are you sure you want to change subscription to ${tierText} for user ${subscriptionUserId}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/change-subscription-tier`, {
        user_identifier: subscriptionUserId,
        new_tier: targetTier
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isGermanTemp 
        ? `Abonnement für ${response.data.user_email} erfolgreich auf ${tierText} geändert` 
        : `Subscription changed successfully to ${tierText} for ${response.data.user_email}`);
      setSubscriptionUserId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isGermanTemp ? 'Fehler beim Ändern des Abonnements' : 'Failed to change subscription tier'));
    }
  };

  const handleImageModelChange = async (model) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/profile`, 
        { image_model_preference: model },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isGerman ? 'Bildmodell aktualisiert' : 'Image model updated');
      if (updateUser) updateUser({ ...user, image_model_preference: model });
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isGerman ? 'Fehler beim Aktualisieren' : 'Failed to update'));
    }
  };

  const handleResolutionChange = async (resolution) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/profile`, 
        { image_resolution_preference: resolution },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isGerman ? 'Auflösung aktualisiert' : 'Resolution updated');
      if (updateUser) updateUser({ ...user, image_resolution_preference: resolution });
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isGerman ? 'Fehler beim Aktualisieren' : 'Failed to update'));
    }
  };

  const isGerman = user?.language === 'de';

  return (
    <div className="settings-page">
      <div className="settings-header">
        <Button variant="outline" onClick={() => navigate('/dashboard')}>
          {isGerman ? '← Zurück' : '← Back'}
        </Button>
      </div>

      <div className="settings-container">
        <h1>{isGerman ? 'Einstellungen' : 'Settings'}</h1>

        {/* Subscription Info */}
        <div className="settings-card">
          <h2>💎 {isGerman ? 'Abonnement' : 'Subscription'}</h2>
          <div className="subscription-info">
            <div className="tier-badge-large">
              {user?.subscription_tier === 'pro' ? '⭐ PRO' : 'FREE'}
            </div>
            {subscriptionInfo && user?.subscription_tier === 'pro' && (
              <div className="subscription-details">
                <p>
                  {isGerman ? 'Status' : 'Status'}: 
                  <span className={subscriptionInfo.status === 'cancelled' ? 'status-cancelled' : 'status-active'}>
                    {subscriptionInfo.status === 'cancelled' 
                      ? (() => {
                          const now = new Date();
                          const endDate = new Date(subscriptionInfo.end_date);
                          const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                          
                          if (daysLeft > 0) {
                            return isGerman 
                              ? ` Gekündigt (noch ${daysLeft} ${daysLeft === 1 ? 'Tag' : 'Tage'} Pro-Zugriff)`
                              : ` Cancelled (${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} of Pro access left)`;
                          } else {
                            return isGerman ? ' Gekündigt (läuft heute ab)' : ' Cancelled (expires today)';
                          }
                        })()
                      : (isGerman ? ' Aktiv' : ' Active')
                    }
                  </span>
                </p>
                {subscriptionInfo.end_date && (
                  <>
                    <p>
                      {isGerman ? 'Gültig bis' : 'Valid until'}: {new Date(subscriptionInfo.end_date).toLocaleDateString(isGerman ? 'de-DE' : 'en-US')}
                    </p>
                    {subscriptionInfo.status === 'cancelled' && (
                      <div className="cancellation-notice">
                        <p className="cancellation-message">
                          ℹ️ {isGerman 
                            ? `Sie können Pro-Funktionen noch bis zum ${new Date(subscriptionInfo.end_date).toLocaleDateString('de-DE')} nutzen. Danach wird Ihr Konto automatisch auf Free heruntergestuft.`
                            : `You can use Pro features until ${new Date(subscriptionInfo.end_date).toLocaleDateString('en-US')}. After that, your account will be automatically downgraded to Free.`
                          }
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {user?.subscription_tier === 'free' ? (
              <Button onClick={() => navigate('/pricing')}>
                {isGerman ? 'Auf Pro upgraden' : 'Upgrade to Pro'}
              </Button>
            ) : (
              <Button variant="outline" onClick={handleCancelSubscription}>
                {isGerman ? 'Abonnement kündigen' : 'Cancel Subscription'}
              </Button>
            )}
          </div>
        </div>

        {/* Profile Settings */}
        <div className="settings-card">
          <h2>👤 {isGerman ? 'Profil' : 'Profile'}</h2>
          <form onSubmit={handleUpdateProfile}>
            <div className="form-group">
              <Label htmlFor="name">{isGerman ? 'Name' : 'Name'}</Label>
              <Input
                id="name"
                type="text"
                value={profile.name}
                onChange={(e) => setProfile({...profile, name: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <Label htmlFor="email">{isGerman ? 'E-Mail' : 'Email'}</Label>
              <Input
                id="email"
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({...profile, email: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <Label htmlFor="language">{isGerman ? 'Sprache' : 'Language'}</Label>
              <Select 
                value={profile.language} 
                onValueChange={(value) => setProfile({...profile, language: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isGerman ? 'Sprache wählen' : 'Select language'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">🇬🇧 English</SelectItem>
                  <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button type="submit">{isGerman ? 'Profil aktualisieren' : 'Update Profile'}</Button>
          </form>
        </div>

        {/* Send to Kindle Settings */}
        <div className="settings-card">
          <h2>📧 {isGerman ? 'An Kindle senden' : 'Send to Kindle'}</h2>
          <form onSubmit={handleUpdateKindleEmail}>
            <div className="form-group">
              <Label htmlFor="kindle_email">{isGerman ? 'Kindle E-Mail Adresse' : 'Kindle Email Address'}</Label>
              <Input
                id="kindle_email"
                type="email"
                value={kindleEmail}
                onChange={(e) => setKindleEmail(e.target.value)}
                placeholder="username@kindle.com"
              />
              <p className="help-text">
                {isGerman 
                  ? 'Hinweis: Du musst die Absender-E-Mail in deinen Amazon Kindle-Einstellungen genehmigen'
                  : 'Note: You\'ll need to approve the sender email in your Amazon Kindle settings'}
              </p>
            </div>
            <Button type="submit">{isGerman ? 'Kindle E-Mail aktualisieren' : 'Update Kindle Email'}</Button>
          </form>
        </div>

        {/* Divider */}
        <div className="section-divider"></div>

        {/* Email Preferences (GDPR Compliance) */}
        <div className="settings-card">
          <h2>🔔 {isGerman ? 'E-Mail-Einstellungen' : 'Email Preferences'}</h2>
          <p>
            {isGerman 
              ? 'Wähle, welche E-Mails du von uns erhalten möchtest. Du kannst dich jederzeit abmelden.'
              : 'Choose which emails you want to receive from us. You can unsubscribe at any time.'}
          </p>
          
          <div className="email-preferences">
            <label className="preference-item">
              <input
                type="checkbox"
                checked={emailPreferences.weekly_reminders}
                onChange={(e) => handleEmailPreferencesChange('weekly_reminders', e.target.checked)}
              />
              <div className="preference-content">
                <strong>
                  {isGerman ? 'Wöchentliche Erinnerungen' : 'Weekly Reminders'}
                </strong>
                <p>
                  {isGerman 
                    ? 'Erhalte Erinnerungen, wenn du noch ungenutzte Geschichten hast (Werbung)'
                    : 'Get reminders when you have unused stories available (promotional)'}
                </p>
              </div>
            </label>

            <label className="preference-item">
              <input
                type="checkbox"
                checked={emailPreferences.story_completion}
                onChange={(e) => handleEmailPreferencesChange('story_completion', e.target.checked)}
              />
              <div className="preference-content">
                <strong>
                  {isGerman ? 'Geschichte fertig-Benachrichtigungen' : 'Story Completion Notifications'}
                </strong>
                <p>
                  {isGerman 
                    ? 'Erhalte eine E-Mail, wenn deine Geschichte erstellt wurde (Transaktional)'
                    : 'Get notified when your story is ready (transactional)'}
                </p>
              </div>
            </label>

            <label className="preference-item">
              <input
                type="checkbox"
                checked={emailPreferences.subscription_updates}
                onChange={(e) => handleEmailPreferencesChange('subscription_updates', e.target.checked)}
              />
              <div className="preference-content">
                <strong>
                  {isGerman ? 'Abo-Updates' : 'Subscription Updates'}
                </strong>
                <p>
                  {isGerman 
                    ? 'Erhalte wichtige Updates zu deinem Abonnement (Transaktional)'
                    : 'Get important updates about your subscription (transactional)'}
                </p>
              </div>
            </label>
          </div>
          
          <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '1rem' }}>
            {isGerman 
              ? '💡 Hinweis: Transaktionale E-Mails (z.B. Passwort-Reset, Kontobestätigung) können nicht deaktiviert werden.'
              : '💡 Note: Transactional emails (e.g., password reset, account verification) cannot be disabled.'}
          </p>
        </div>

        {/* Data Export (GDPR) */}
        <div className="settings-card">
          <h2>📥 {isGerman ? 'Datenexport' : 'Data Export'}</h2>
          <p>
            {isGerman 
              ? 'Lade alle deine Kontodaten und Geschichten als JSON-Datei herunter (DSGVO Recht auf Datenübertragbarkeit).'
              : 'Download all your account data and stories as a JSON file (GDPR Right to Data Portability).'}
          </p>
          <Button onClick={handleExportData} variant="outline">
            📥 {isGerman ? 'Daten exportieren' : 'Export Data'}
          </Button>
        </div>

        {/* Admin Analytics Dashboard */}
        {user?.is_admin && (
          <div className="settings-card">
            <AdminAnalytics />
          </div>
        )}

        {/* Admin Referrals & Guest Emails */}
        {user?.is_admin && (
          <div className="settings-card">
            <AdminReferrals />
          </div>
        )}

        {/* Admin User Management */}
        {user?.is_admin && (
          <div className="settings-card">
            <h2>👥 {isGerman ? 'Benutzerverwaltung' : 'User Management'}</h2>
            
            {/* Reset Story Count */}
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px' }}>
                {isGerman ? 'Story-Anzahl zurücksetzen' : 'Reset Story Count'}
              </h3>
              <p className="feature-description">
                {isGerman 
                  ? 'Setze die wöchentliche Story-Anzahl für einen bestimmten Benutzer zurück.'
                  : 'Reset the weekly story count for a specific user.'}
              </p>
              <form onSubmit={handleResetUserStoryCount}>
                <div className="form-group">
                  <Label htmlFor="reset_user_id">
                    {isGerman ? 'Benutzer-ID oder E-Mail' : 'User ID or Email'}
                  </Label>
                  <Input
                    id="reset_user_id"
                    type="text"
                    value={resetUserId}
                    onChange={(e) => setResetUserId(e.target.value)}
                    placeholder={isGerman ? 'Benutzer-ID oder E-Mail eingeben' : 'Enter user ID or email'}
                    required
                  />
                </div>
                <Button type="submit" variant="outline">
                  🔄 {isGerman ? 'Story-Anzahl zurücksetzen' : 'Reset Story Count'}
                </Button>
              </form>
            </div>

            {/* Grant Bonus Stories */}
            <div style={{ marginTop: '30px', paddingTop: '30px', borderTop: '1px solid #eee' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px' }}>
                🎁 {isGerman ? 'Bonus-Geschichten vergeben' : 'Grant Bonus Stories'}
              </h3>
              <p className="feature-description">
                {isGerman 
                  ? 'Vergebe einmalige Bonus-Geschichten an einen Benutzer. Diese werden NICHT monatlich zurückgesetzt und werden einzeln verbraucht.'
                  : 'Grant one-time bonus stories to a user. These do NOT reset monthly and are consumed one by one.'}
              </p>
              <form onSubmit={handleGrantBonusStories}>
                <div className="form-group">
                  <Label htmlFor="bonus_user_id">
                    {isGerman ? 'Benutzer-ID oder E-Mail' : 'User ID or Email'}
                  </Label>
                  <Input
                    id="bonus_user_id"
                    type="text"
                    value={bonusUserId}
                    onChange={(e) => setBonusUserId(e.target.value)}
                    placeholder={isGerman ? 'Benutzer-ID oder E-Mail eingeben' : 'Enter user ID or email'}
                    required
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="bonus_amount">
                    {isGerman ? 'Anzahl Bonus-Geschichten' : 'Number of Bonus Stories'}
                  </Label>
                  <Input
                    id="bonus_amount"
                    type="number"
                    min="1"
                    max="100"
                    value={bonusAmount}
                    onChange={(e) => setBonusAmount(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" variant="outline">
                  🎁 {isGerman ? 'Bonus-Geschichten vergeben' : 'Grant Bonus Stories'}
                </Button>
              </form>
            </div>

            {/* Change Subscription Tier */}
            <div style={{ marginTop: '30px', paddingTop: '30px', borderTop: '1px solid #eee' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px' }}>
                {isGerman ? 'Abonnement-Typ ändern' : 'Change Subscription Tier'}
              </h3>
              <p className="feature-description">
                {isGerman 
                  ? 'Ändere das Abonnement eines Benutzers zwischen Free und Pro.'
                  : 'Change a user\'s subscription between Free and Pro.'}
              </p>
              <form onSubmit={handleChangeSubscriptionTier}>
                <div className="form-group">
                  <Label htmlFor="subscription_user_id">
                    {isGerman ? 'Benutzer-ID oder E-Mail' : 'User ID or Email'}
                  </Label>
                  <Input
                    id="subscription_user_id"
                    type="text"
                    value={subscriptionUserId}
                    onChange={(e) => setSubscriptionUserId(e.target.value)}
                    placeholder={isGerman ? 'Benutzer-ID oder E-Mail eingeben' : 'Enter user ID or email'}
                    required
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="target_tier">
                    {isGerman ? 'Ziel-Abonnement' : 'Target Subscription'}
                  </Label>
                  <select
                    id="target_tier"
                    value={targetTier}
                    onChange={(e) => setTargetTier(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="pro">Pro (⚡ Premium)</option>
                    <option value="free">Free (🆓 Basic)</option>
                  </select>
                </div>
                <Button type="submit" variant="outline">
                  ⚡ {isGerman ? 'Abonnement ändern' : 'Change Subscription'}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* My Children - Available for All Users */}
        <div className="settings-card">
          <MyChildren user={user} language={user?.language || 'en'} />
        </div>

        {/* Admin Image Model & Resolution Settings */}
        {user?.is_admin && (
          <div className="settings-card">
            <h2>
              🎨 {isGerman ? 'Bildgenerierungs-Einstellungen' : 'Image Generation Settings'}
              <span className="pro-badge">⚡ ADMIN</span>
            </h2>
            <p className="feature-description">
              {isGerman 
                ? 'Wähle das KI-Modell und die Auflösung für die Bildgenerierung. Diese Einstellungen überschreiben die Standard-Konfiguration.'
                : 'Choose the AI model and resolution for image generation. These settings override the default configuration.'
              }
            </p>

            <div className="admin-image-settings">
              <div className="setting-group">
                <Label>{isGerman ? 'Modell' : 'Model'}</Label>
                <div className="model-options">
                  <div 
                    className={`model-option ${(user?.image_model_preference || 'NANOBANA') === 'gemini-3.1-flash-image-preview' ? 'selected' : ''}`}
                    onClick={() => handleImageModelChange('gemini-3.1-flash-image-preview')}
                  >
                    <input 
                      type="radio" 
                      id="gemini_3_1_flash_image_preview_admin" 
                      name="imageModelAdmin" 
                      value="gemini-3.1-flash-image-preview"
                      checked={(user?.image_model_preference || 'NANOBANA') === 'gemini-3.1-flash-image-preview'}
                      onChange={() => handleImageModelChange('gemini-3.1-flash-image-preview')}
                    />
                    <label htmlFor="gemini_3_1_flash_image_preview_admin">
                      <span className="model-name">✨ Gemini 3.1 Flash Image Preview</span>
                      <span className="model-desc">
                        {isGerman ? 'Neuer Gemini-Flash-Bildmodus' : 'New Gemini Flash image mode'}
                      </span>
                    </label>
                  </div>

                  <div 
                    className={`model-option ${(user?.image_model_preference || 'NANOBANA') === 'NANOBANA_PRO' ? 'selected' : ''}`}
                    onClick={() => handleImageModelChange('NANOBANA_PRO')}
                  >
                    <input 
                      type="radio" 
                      id="nanobana_pro_admin" 
                      name="imageModelAdmin" 
                      value="NANOBANA_PRO"
                      checked={(user?.image_model_preference || 'NANOBANA') === 'NANOBANA_PRO'}
                      onChange={() => handleImageModelChange('NANOBANA_PRO')}
                    />
                    <label htmlFor="nanobana_pro_admin">
                      <span className="model-name">🍌✨ Nano Banana Pro</span>
                      <span className="model-desc">
                        {isGerman ? 'Beste Qualität, Gemini 3 Pro' : 'Best quality, Gemini 3 Pro'}
                      </span>
                    </label>
                  </div>

                  <div 
                    className={`model-option ${(user?.image_model_preference || 'NANOBANA') === 'NANOBANA' ? 'selected' : ''}`}
                    onClick={() => handleImageModelChange('NANOBANA')}
                  >
                    <input 
                      type="radio" 
                      id="nanobana_admin" 
                      name="imageModelAdmin" 
                      value="NANOBANA"
                      checked={(user?.image_model_preference || 'NANOBANA') === 'NANOBANA'}
                      onChange={() => handleImageModelChange('NANOBANA')}
                    />
                    <label htmlFor="nanobana_admin">
                      <span className="model-name">🍌 Nano Banana</span>
                      <span className="model-desc">
                        {isGerman ? 'Cartoon-Stil, mittel' : 'Cartoon style, medium cost'}
                      </span>
                    </label>
                  </div>

                  <div 
                    className={`model-option ${(user?.image_model_preference || 'NANOBANA') === 'DALLE' ? 'selected' : ''}`}
                    onClick={() => handleImageModelChange('DALLE')}
                  >
                    <input 
                      type="radio" 
                      id="dalle_admin" 
                      name="imageModelAdmin" 
                      value="DALLE"
                      checked={(user?.image_model_preference || 'NANOBANA') === 'DALLE'}
                      onChange={() => handleImageModelChange('DALLE')}
                    />
                    <label htmlFor="dalle_admin">
                      <span className="model-name">🎨 DALL-E 3</span>
                      <span className="model-desc">
                        {isGerman ? 'Hochwertig, teuer' : 'High quality, expensive'}
                      </span>
                    </label>
                  </div>

                  <div 
                    className={`model-option ${(user?.image_model_preference || 'NANOBANA') === 'SDXL_TURBO' ? 'selected' : ''}`}
                    onClick={() => handleImageModelChange('SDXL_TURBO')}
                  >
                    <input 
                      type="radio" 
                      id="sdxl_admin" 
                      name="imageModelAdmin" 
                      value="SDXL_TURBO"
                      checked={(user?.image_model_preference || 'NANOBANA') === 'SDXL_TURBO'}
                      onChange={() => handleImageModelChange('SDXL_TURBO')}
                    />
                    <label htmlFor="sdxl_admin">
                      <span className="model-name">⚡ SDXL Turbo</span>
                      <span className="model-desc">
                        {isGerman ? 'Schnell, günstig' : 'Fast, cheap'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="setting-group">
                <Label htmlFor="resolution_select">{isGerman ? 'Auflösung' : 'Resolution'}</Label>
                <select
                  id="resolution_select"
                  className="resolution-select"
                  value={user?.image_resolution_preference || '1024x1024'}
                  onChange={(e) => handleResolutionChange(e.target.value)}
                >
                  <option value="512x512">512x512 ({isGerman ? 'Günstiger' : 'Cheaper'})</option>
                  <option value="1024x1024">1024x1024 ({isGerman ? 'Höhere Qualität' : 'Higher Quality'})</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Admin Theme Settings */}
        {user?.is_admin && (
          <div className="settings-card">
            <h2>
              🎨 {isGerman ? 'App-Design' : 'App Theme'}
              <span className="pro-badge">⚡ ADMIN</span>
            </h2>
            <p className="feature-description">
              {isGerman 
                ? 'Wähle das globale Design für die gesamte Anwendung. Änderungen gelten für alle Benutzer.'
                : 'Choose the global theme for the entire application. Changes apply to all users.'
              }
            </p>

            <div className="theme-selector" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
              gap: '1.5rem', 
              marginTop: '1.5rem' 
            }}>
              {Object.values(themes).map((themeOption) => (
                <div 
                  key={themeOption.id}
                  className={`theme-option ${theme === themeOption.id ? 'selected' : ''}`}
                  style={{
                    position: 'relative',
                    padding: '1.5rem',
                    border: `3px solid ${theme === themeOption.id ? '#14B8A6' : 'var(--theme-input-border, #E5E7EB)'}`,
                    borderRadius: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: theme === themeOption.id ? 'var(--theme-bg-hover, #FFF0F5)' : 'var(--theme-bg-card, white)'
                  }}
                  onClick={async () => {
                    if (theme === themeOption.id || savingTheme) return;
                    setSavingTheme(true);
                    const token = localStorage.getItem('token');
                    const result = await updateTheme(themeOption.id, token);
                    if (result.success) {
                      toast.success(isGerman ? 'Design aktualisiert!' : 'Theme updated!');
                    } else {
                      toast.error(result.error || (isGerman ? 'Fehler beim Aktualisieren' : 'Failed to update'));
                    }
                    setSavingTheme(false);
                  }}
                  onMouseEnter={(e) => {
                    if (theme !== themeOption.id) {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.borderColor = '#14B8A6';
                      e.currentTarget.style.boxShadow = '0 8px 30px rgba(20, 184, 166, 0.22)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (theme !== themeOption.id) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = 'var(--theme-input-border, #E5E7EB)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  <div className="theme-preview" style={{ 
                    width: '100%',
                    height: '80px',
                    borderRadius: '12px',
                    border: `2px solid ${themeOption.preview.primary}`,
                    position: 'relative',
                    overflow: 'hidden',
                    marginBottom: '1rem',
                    background: themeOption.preview.bg
                  }}>
                    <div className="theme-preview-accent" style={{ 
                      position: 'absolute',
                      width: '60%',
                      height: '30px',
                      borderRadius: '8px',
                      top: '15px',
                      left: '15px',
                      background: themeOption.preview.primary 
                    }}></div>
                    <div className="theme-preview-secondary" style={{ 
                      position: 'absolute',
                      width: '40%',
                      height: '20px',
                      borderRadius: '6px',
                      bottom: '15px',
                      right: '15px',
                      background: themeOption.preview.secondary 
                    }}></div>
                  </div>
                  <div className="theme-info" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span className="theme-icon" style={{ fontSize: '1.5rem' }}>{themeOption.icon}</span>
                    <span className="theme-name" style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--theme-text-primary, #1F2937)' }}>
                      {isGerman ? themeOption.name.de : themeOption.name.en}
                    </span>
                    <span className="theme-desc" style={{ fontSize: '0.875rem', color: 'var(--theme-text-secondary, #6B7280)' }}>
                      {isGerman ? themeOption.description.de : themeOption.description.en}
                    </span>
                  </div>
                  {theme === themeOption.id && (
                    <div className="theme-active-badge" style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '1rem',
                      background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}>
                      {isGerman ? 'Aktiv' : 'Active'}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {savingTheme && (
              <p className="saving-indicator">
                {isGerman ? 'Speichern...' : 'Saving...'}
              </p>
            )}
          </div>
        )}

        {/* Admin Blog Management */}
        {user?.is_admin && (
          <div className="settings-card">
            <AdminBlogManagement />
          </div>
        )}

        {/* Divider */}
        <div className="section-divider"></div>

        {/* Danger Zone */}
        <div className="settings-card danger-zone">
          <h2>⚠️ {isGerman ? 'Gefahrenzone' : 'Danger Zone'}</h2>
          <p>
            {isGerman 
              ? 'Sobald du dein Konto löschst, gibt es kein Zurück mehr. Alle deine Geschichten werden dauerhaft gelöscht.'
              : 'Once you delete your account, there is no going back. All your stories will be permanently deleted.'}
          </p>
          <Button variant="destructive" onClick={handleDeleteAccount}>
            🗑️ {isGerman ? 'Konto löschen' : 'Delete Account'}
          </Button>
        </div>

        <div className="settings-actions">
          <Button variant="outline" onClick={logout}>
            {isGerman ? 'Abmelden' : 'Logout'}
          </Button>
        </div>
      </div>
    </div>
  );
}
